import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { skipToken } from '@reduxjs/toolkit/query';

import { clearInventering, setRegistrationActive } from 'store/aktivitetSlice';
import { clearBebyggelseobjektUtkast, getBebyggelseobjektUtkast } from 'store/bebyggelseobjektUtkastSlice';
import { setFocusOnExtent, setMapMode } from 'store/mapSlice';
import {
  clearPubliceradeBebyggelseobjekt,
  getPubliceradeBebyggelseobjekt
} from 'store/publiceradeBebyggelseobjektSlice';
import { RootState } from 'store/rootReducer';
import { clearUtkast } from 'store/utkastSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';
import { useDrawerMobileTemplate } from 'hooks/useDrawerMobileTemplate';
import { useInitialFocusOnExtent } from 'hooks/useInitialFocusOnExtent';
import { useMapInteraction } from 'hooks/useMapInteraction';

import { RaaDialog, RaaSpinner } from 'raa-react';

import { CenterActiveSourceGeometryButton } from 'components/CenterActiveSourceGeometryButton/CenterActiveSourceGeometryButton';
import DeleteInventeringPreliminarDialog from 'components/aktivitetRegistrering/inventering/deleteDialogs/DeleteInventeringPreliminarDialog';
import {
  DrawerBody,
  DrawerContainer,
  DrawerFooter,
  DrawerSection,
  NavigationDrawerHead,
  SpinnerContainer
} from 'components/drawerSections/DrawerSections';
import RaaInputField from 'components/fieldComponents/raaInputField/RaaInputField';
import RaaTextAreaField from 'components/fieldComponents/raaTextAreaField/RaaTextAreaField';
import ScrollToError from 'components/fieldComponents/scrollToError/ScrollToError';
import GeometryFileDownload from 'components/geometryFileDownload/GeometryFileDownload';
import { GeometryFileUploadInput } from 'components/geometryFileUpload/GeometryFileUpload';
import { activeSource, replaceInventeringInActiveSource } from 'components/map/Layers';
import { Colors } from 'components/map/Styles';
import { inventeringCircle } from 'components/map/icons';
import { TextButton } from 'components/sharedElements/sharedElements';

import {
  BebyggelseobjektPubliceradIndex,
  BebyggelseobjektUtkastIndex,
  HTTP_STATUS,
  InventeringPost,
  InventeringPreliminar,
  InventeringPut
} from 'models';

import { AktivitetRegistreringsstatustypEnum, AktivitetStatusEnum, MapModeEnum } from 'enums';

import { Feature } from 'ol';
import GeoJSON, { GeoJSONFeature, GeoJSONFeatureCollection } from 'ol/format/GeoJSON';

import { FormValues, inventeringFormSchema } from './inventeringFormSchema';

import { intersect } from '@turf/turf';

import {
  useGetAktivitetPreliminarQuery,
  usePostAktivitetInventeringPreliminarMutation,
  usePutAktivitetInventeringPreliminarMutation
} from 'api/endpoints/aktivitetPreliminarSlice';
import { useBlockerContext } from 'context/navigationBlockContext';
import { ErrorMessage, Form, Formik, FormikHelpers, FormikValues } from 'formik';
import { setApiValidationErrors } from 'utilities/formUtilities';
import { hasSelfIntersections } from 'utilities/hasSelfIntersections';
import { isFetchBaseQueryModelError } from 'utilities/typeGuards';

const InventeringRegistration: FC = () => {
  const { id } = useParams();
  const { setDialog, unsetDialog } = useDialog();
  const { setInteractionOptions, unsetInteractionOptions } = useMapInteraction();
  const aktivitetStatusTyper = useAppSelector((state: RootState) => state.aktivitetDomaner.data?.aktivitetstatustyper);
  const { data: aktivitetDomaner } = useAppSelector((state: RootState) => state.aktivitetDomaner);
  const { selected_organisation: selectedOrganisation } = useAppSelector((state: RootState) => state.user);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState<FormValues>();
  const [convertingFile, setConvertingFile] = useState<boolean>(false);
  const [dirtyForm, setDirtyForm] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deleteSuccessful, setDeleteSuccessful] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const fileUploadRef = useRef<HTMLInputElement>(null);
  const formikHelpersRef = useRef<FormikHelpers<FormValues>>();
  const { setNavigationBlocker } = useBlockerContext();

  const [
    postAktivitetInventeringPreliminar,
    {
      data: postInventeringResponseData,
      isSuccess: postInventeringIsSuccess,
      error: postInventeringError,
      isError: postInventeringIsError
    }
  ] = usePostAktivitetInventeringPreliminarMutation();

  const [
    putAktivitetInventeringPreliminar,
    {
      data: putInventeringResponseData,
      isSuccess: putInventeringIsSuccess,
      error: putInventeringError,
      isError: putInventeringIsError
    }
  ] = usePutAktivitetInventeringPreliminarMutation();

  const {
    data: inventeringPreliminarData,
    error: inventeringPreliminarError,
    isLoading: inventeringPreliminarIsLoading
  } = useGetAktivitetPreliminarQuery(id ?? skipToken, {
    refetchOnMountOrArgChange: true,
    skip: deleteSuccessful || deleteLoading
  });

  const hasMatchingId = !!(
    id &&
    inventeringPreliminarData?.aktivitet_id &&
    id === inventeringPreliminarData?.aktivitet_id
  );

  const setInitialFormValues = useCallback(
    (inventering: InventeringPreliminar | undefined) => {
      setInitialValues({
        titel: inventering ? inventering.titel : '',
        syfte: inventering ? inventering.syfte : '',
        start_ar: inventering ? inventering.start_ar : '',
        slut_ar: inventering?.slut_ar ? inventering.slut_ar : '',
        inventeringsomrade: inventering ? inventering.inventeringsomrade : ''
      });
    },
    [setInitialValues]
  );

  const { setDrawerMobileTemplate } = useDrawerMobileTemplate();

  useEffect(() => {
    if (
      inventeringPreliminarError &&
      'status' in inventeringPreliminarError &&
      inventeringPreliminarError.status === HTTP_STATUS.NOT_FOUND
    ) {
      const aktivitetId = 'args' in inventeringPreliminarError ? (inventeringPreliminarError.args as any).id : id;
      navigate(`/aktivitet/${aktivitetId}`, { replace: true });
    } else {
      dispatch(clearBebyggelseobjektUtkast());
      dispatch(clearPubliceradeBebyggelseobjekt());
      dispatch(setMapMode(MapModeEnum.INVENTERING_PRESENTATION));
    }
  }, [dispatch, navigate, inventeringPreliminarError, id]);

  useEffect(() => {
    dispatch(setRegistrationActive(true));
    dispatch(setMapMode(MapModeEnum.INVENTERING_REGISTRATION));

    // Cleanup
    return () => {
      setDrawerMobileTemplate(undefined);
      dispatch(clearInventering());
      dispatch(clearUtkast());
      dispatch(clearPubliceradeBebyggelseobjekt());
      dispatch(clearBebyggelseobjektUtkast());
      dispatch(setMapMode(MapModeEnum.DEFAULT));
      dispatch(setRegistrationActive(false));
      activeSource.clear();
      activeSource.changed();
    };
  }, [dispatch, setDrawerMobileTemplate]);

  useEffect(() => {
    if (id && selectedOrganisation) {
      setInitialFormValues(inventeringPreliminarData);
      if (inventeringPreliminarData) {
        if (
          inventeringPreliminarData.aktivitetstatus.status !== AktivitetStatusEnum.PLANERAD ||
          inventeringPreliminarData.aktivitet_registrering_status.registreringsstatustyp_id !==
            AktivitetRegistreringsstatustypEnum.REDIGERBAR ||
          inventeringPreliminarData.ansvarig_organisation.organisationsnummer !==
            selectedOrganisation?.organisation_nummer
        ) {
          navigate(`/inventering/${inventeringPreliminarData.aktivitet_id}/preliminar`);
        }

        dispatch(getBebyggelseobjektUtkast(inventeringPreliminarData.aktivitet_id));
        dispatch(getPubliceradeBebyggelseobjekt(inventeringPreliminarData.aktivitet_id));
      }
    } else {
      dispatch(clearInventering());
      dispatch(clearBebyggelseobjektUtkast());
      dispatch(clearPubliceradeBebyggelseobjekt());
      setInitialFormValues(undefined);
    }
  }, [dispatch, id, inventeringPreliminarData, navigate, selectedOrganisation, setInitialFormValues]);

  useEffect(() => {
    if (
      inventeringPreliminarError &&
      'status' in inventeringPreliminarError &&
      inventeringPreliminarError?.status === HTTP_STATUS.PRECONDITION_FAILED
    ) {
      requestAnimationFrame(() => {
        setDialog(RaaDialog, {
          header: 'Dina ändringar kan inte sparas',
          proceed: () => location.reload(),
          proceedButtonText: 'Avbryt redigeringen',
          ariaDescribedBy: 'dialog-description',
          children: (
            <p id="dialog-description" className="tw-mt-4">
              En annan användare har ändrat inventeringens information. Avbryt redigeringen och börja om från den nya
              versionen.
            </p>
          )
        });
      });
    }
  }, [inventeringPreliminarError, setDialog]);

  useInitialFocusOnExtent(
    useCallback(
      () =>
        inventeringPreliminarData?.inventeringsomrade &&
        replaceInventeringInActiveSource({
          featureCollection: inventeringPreliminarData.inventeringsomrade,
          inventering: inventeringPreliminarData
        }),
      [inventeringPreliminarData]
    )
  );

  useEffect(() => {
    if (postInventeringIsError || putInventeringIsError || postInventeringIsSuccess || putInventeringIsSuccess) {
      setSubmitting(false);
    }
  }, [postInventeringIsError, putInventeringIsError, postInventeringIsSuccess, putInventeringIsSuccess]);

  useEffect(() => {
    const blocked = !submitting && !inventeringPreliminarIsLoading && dirtyForm;
    setNavigationBlocker({
      message: 'Vill du slänga ändringarna?',
      blocked,
      proceedText: 'släng',
      cancelText: 'avbryt'
    });
  }, [dirtyForm, inventeringPreliminarIsLoading, setNavigationBlocker, submitting]);

  useEffect(() => {
    setDrawerMobileTemplate(
      <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
        <h2 className="tw-heading-lg">{hasMatchingId ? inventeringPreliminarData?.titel : 'Ny inventering'}</h2>
        <div className="tw-flex tw-items-center">
          <img
            src={'data:image/svg+xml,' + encodeURIComponent(inventeringCircle(Colors.RAA_BLUE, Colors.WHITE))}
            alt="Ikon för inventering"
          />
          <p className="tw-ml-2">
            Inventering {inventeringPreliminarData ? inventeringPreliminarData.aktivitetsnummer : '(ej sparad)'}
          </p>
        </div>
      </div>
    );
  }, [hasMatchingId, inventeringPreliminarData, setDrawerMobileTemplate]);

  const submitForm = async (values: FormikValues, formikHelpers: FormikHelpers<FormValues>) => {
    if (submitting) return;

    setSubmitting(true);

    formikHelpersRef.current = formikHelpers;

    if (id && inventeringPreliminarData?.aktivitet_id) {
      const put: InventeringPut = {
        titel: values.titel.trim(),
        syfte: values.syfte.trim(),
        start_ar: values.start_ar,
        slut_ar: values.slut_ar || null,
        inventeringsomrade: values.inventeringsomrade
      };

      void putAktivitetInventeringPreliminar({
        aktivitetId: inventeringPreliminarData.aktivitet_id,
        inventering: put,
        eTag: inventeringPreliminarData.eTag!
      });
    } else {
      const post: InventeringPost = {
        titel: values.titel.trim(),
        syfte: values.syfte.trim(),
        start_ar: values.start_ar,
        slut_ar: values.slut_ar || undefined,
        inventeringsomrade: values.inventeringsomrade,
        organisationsnummer: (selectedOrganisation && selectedOrganisation.organisation_nummer) || ''
      };

      void postAktivitetInventeringPreliminar(post);
    }
  };

  useEffect(() => {
    if (
      (postInventeringIsSuccess && postInventeringResponseData) ||
      (putInventeringIsSuccess && putInventeringResponseData)
    ) {
      formikHelpersRef.current?.resetForm();

      if (postInventeringIsSuccess && postInventeringResponseData) {
        navigate(`/inventering/${postInventeringResponseData?.aktivitet_id}/preliminar`);
      }

      if (putInventeringIsSuccess && putInventeringResponseData) {
        navigate(`/inventering/${putInventeringResponseData?.aktivitet_id}/preliminar`);
      }
    }
  }, [
    navigate,
    postInventeringIsSuccess,
    postInventeringResponseData,
    putInventeringIsSuccess,
    putInventeringResponseData
  ]);

  useEffect(() => {
    const setFieldError = formikHelpersRef.current?.setFieldError;
    if (postInventeringIsError && setFieldError && isFetchBaseQueryModelError(postInventeringError)) {
      setApiValidationErrors(postInventeringError.data, setFieldError);
    }

    if (putInventeringIsError && setFieldError && isFetchBaseQueryModelError(putInventeringError)) {
      setApiValidationErrors(putInventeringError.data, setFieldError);
    }
  }, [postInventeringIsError, postInventeringError, putInventeringIsError, putInventeringError]);

  const noGeometryMessage = {
    blocked: true,
    header: `Inventeringsområdet innehåller ingen geometri`,
    message: 'Lägg till en geometri för att kunna spara det nya området.'
  };

  const selfIntersectionMessage = {
    blocked: true,
    header: `Inventeringsområdet innehåller en självkorsande geometri`,
    message: 'Ta bort korsningen för att kunna spara det nya området.'
  };

  const intersectingGeometriesMessage = {
    blocked: true,
    header: `Inventeringsområdet innehåller minst en överlappande geometri`,
    message: 'Ta bort eller ändra den överlappande geometrin för att kunna spara det nya området.'
  };

  const utkastOutsideMessage = {
    blocked: true,
    header: `Det finns utkast utanför inventeringsområdet`,
    message: 'Radera dessa för att kunna spara det nya området.'
  };

  const handleEditGeometry = (
    featureCollection: GeoJSONFeatureCollection,
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    publiceradeBebyggelseobjekt: Array<BebyggelseobjektPubliceradIndex> | undefined,
    bebyggelseobjektUtkast: Array<BebyggelseobjektUtkastIndex> | undefined
  ) => {
    setInteractionOptions({
      geometry: featureCollection,
      onProceed: (featureCollection: GeoJSONFeatureCollection) =>
        handleInteractionProceed(inventeringPreliminarData, featureCollection, setFieldTouched, setFieldValue),
      onProceedGuard: (featureCollection: GeoJSONFeatureCollection) => {
        const geoJson = new GeoJSON();
        const features = featureCollection.features;

        if (!features || features.length === 0) {
          return noGeometryMessage;
        }

        const blockedBySelfIntersections = hasSelfIntersections(featureCollection, geoJson);

        if (blockedBySelfIntersections) {
          return selfIntersectionMessage;
        }

        const blockedByIntersectingGeometries = features.some((featOuter: GeoJSONFeature) =>
          features.some((featInner: GeoJSONFeature) => {
            const intersection = featOuter.geometry !== featInner.geometry ? intersect(featOuter, featInner) : null;
            return intersection !== null;
          })
        );

        if (blockedByIntersectingGeometries) {
          return intersectingGeometriesMessage;
        }

        // Åtminstone EN del av varje utkast måste ligga inuti en av de inventerade områdena (delmängder måste överlappa minst en gång)
        const blockedByUtkast = bebyggelseobjektUtkast?.some((utkast) => {
          return utkast.geometri?.features.some((utkastFeature: GeoJSONFeature) => {
            return !features.some((feature: GeoJSONFeature) => intersect(utkastFeature, feature) !== null);
          });
        });

        if (blockedByUtkast) {
          return utkastOutsideMessage;
        }

        // Åtminstone EN del av varje publicerat BO måste ligga inuti en av de inventerade områdena (delmängder måste överlappa minst en gång)
        const blockedByPublicerade = !!publiceradeBebyggelseobjekt?.some((publicerad) => {
          return publicerad.geometri?.features.some((publiceradFeature: GeoJSONFeature) => {
            return !features.some((feature: GeoJSONFeature) => intersect(publiceradFeature, feature) !== null);
          });
        });

        return {
          blocked: blockedByPublicerade,
          header: `Det finns inventerade objekt utanför inventeringsområdet`,
          message: 'Radera dessa för att kunna spara det nya området.'
        };
      }
    });
  };

  const handleInteractionProceed = (
    inventering: InventeringPreliminar | undefined,
    featureCollection: GeoJSONFeatureCollection,
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    if (featureCollection) {
      setFieldTouched('inventeringsomrade');
      setFieldValue('inventeringsomrade', featureCollection);
      replaceInventeringInActiveSource({
        featureCollection: featureCollection,
        inventering: inventering
      });
      dispatch(
        setFocusOnExtent({
          type: 'fit',
          extent: activeSource.getExtent()
        })
      );
    }

    dispatch(setMapMode(MapModeEnum.INVENTERING_REGISTRATION));
    unsetInteractionOptions();
  };

  const getGeometryName = (
    inventering: InventeringPreliminar | undefined,
    geometryTouched: boolean | undefined
  ): string => {
    if (inventering?.inventeringsomrade_datum && !geometryTouched) {
      return `${inventering.aktivitetsnummer} ${inventering.inventeringsomrade_datum}`;
    } else if (inventering?.inventeringsomrade_datum && geometryTouched) {
      return `${inventering.aktivitetsnummer} ${new Date().toLocaleDateString('sv-SE')}`;
    } else {
      return new Date().toLocaleDateString('sv-SE');
    }
  };

  return (
    <SpinnerContainer showSpinner={inventeringPreliminarIsLoading && !inventeringPreliminarData}>
      {initialValues && aktivitetStatusTyper && (
        <DrawerContainer>
          <NavigationDrawerHead />
          <DrawerBody>
            <DrawerSection>
              <h1 className="tw-heading-lg tw-mb-6">
                {hasMatchingId ? inventeringPreliminarData?.titel : 'Ny inventering'}
              </h1>
              <div className="tw-mb-4 tw-flex tw-items-center">
                <img
                  src={'data:image/svg+xml,' + encodeURIComponent(inventeringCircle(Colors.RAA_BLUE, Colors.WHITE))}
                  alt="Ikon för inventering"
                />
                <p className="tw-ml-2">
                  Inventering {inventeringPreliminarData ? inventeringPreliminarData.aktivitetsnummer : '(ej sparad)'}
                </p>
              </div>

              <p className="tw-mb-4">
                {
                  aktivitetDomaner?.aktivitettyper.find(
                    (aktivitettyp) => aktivitettyp.aktivitettyp === inventeringPreliminarData?.aktivitetstyp
                  )?.beskrivning
                }
              </p>

              <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={inventeringFormSchema}
                onSubmit={submitForm}
                innerRef={(ref) => setDirtyForm(ref?.dirty || false)}
              >
                {({ errors, touched, setFieldTouched, setFieldValue, values }) => (
                  <Form id="inventeringForm" data-testid="inventering-registration-form">
                    <div className="tw-mb-5">
                      <RaaInputField
                        name="titel"
                        label="Titel"
                        placeholder="Skriv en kort och beskrivande titel"
                        shouldValidateForm={true}
                      />
                    </div>
                    <div className="tw-mb-5">
                      <h2 className="raa-label">Inventeringsyta</h2>

                      <div className="tw-flex tw-justify-between" id="inventeringsomrade">
                        {convertingFile && <span className="tw-italic tw-text-raa-gray-5">Konverterar filen...</span>}
                        {!convertingFile && (
                          <div className="tw-flex tw-gap-2">
                            <TextButton
                              icon="raa-icon-edit"
                              onClick={() =>
                                handleEditGeometry(
                                  values.inventeringsomrade,
                                  setFieldTouched,
                                  setFieldValue,
                                  undefined,
                                  undefined
                                )
                              }
                            >
                              {values.inventeringsomrade ? 'Ändra' : 'Rita yta'}
                            </TextButton>

                            <GeometryFileUploadInput
                              converting={(converting: boolean) => setConvertingFile(converting)}
                              success={(features: Feature[]) =>
                                handleInteractionProceed(
                                  inventeringPreliminarData,
                                  features,
                                  setFieldTouched,
                                  setFieldValue
                                )
                              }
                              labelText={values.inventeringsomrade ? 'Ny från fil' : 'Från fil'}
                              showReplaceWarning={!!values.inventeringsomrade}
                              fileUploadRef={fileUploadRef}
                            />
                          </div>
                        )}
                        <div className="tw-flex tw-gap-2">
                          {values.inventeringsomrade && !convertingFile && (
                            <>
                              <CenterActiveSourceGeometryButton />
                              <GeometryFileDownload
                                fileName={getGeometryName(inventeringPreliminarData, touched.inventeringsomrade)}
                                geometry={values.inventeringsomrade}
                              />
                            </>
                          )}
                        </div>
                      </div>

                      <ErrorMessage
                        name="inventeringsomrade"
                        component="p"
                        render={(message) => (
                          <p className="tw-mt-1 tw-whitespace-pre-line tw-text-raa-error-red">{message}</p>
                        )}
                      />
                    </div>

                    <div className="tw-mb-5">
                      <h3 className="raa-label">Ansvarig organisation</h3>
                      <p>
                        {inventeringPreliminarData
                          ? inventeringPreliminarData.ansvarig_organisation.organisationsnamn
                          : selectedOrganisation && selectedOrganisation.organisation_namn}
                      </p>
                    </div>

                    <label className="raa-label" htmlFor="start_ar">
                      Inventeringsår
                    </label>
                    <div className="tw-flex tw-items-center">
                      <div className="tw-w-[6.4rem]">
                        <RaaInputField name="start_ar" maxLength={4} showError={false} shouldValidateForm={true} />
                      </div>
                      <span className="tw-mx-2">till</span>
                      <div className="tw-w-[6.4rem]">
                        <label className="tw-sr-only" htmlFor="slut_ar">
                          Inventeringsår till och med
                        </label>
                        <RaaInputField name="slut_ar" maxLength={4} showError={false} shouldValidateForm={true} />
                      </div>
                    </div>
                    <p className="tw-mt-1 tw-flex tw-min-h-[1.5rem] tw-flex-col tw-text-raa-error-red">
                      <span>{errors.start_ar && touched.start_ar && errors.start_ar}</span>
                      <span>{errors.slut_ar && touched.slut_ar && errors.slut_ar}</span>
                    </p>
                    <RaaTextAreaField
                      name="syfte"
                      label="Syfte och metod med inventeringen"
                      placeholder="Beskriv inventeringens syfte och metod"
                    />
                    <ScrollToError />
                  </Form>
                )}
              </Formik>
            </DrawerSection>
          </DrawerBody>
          <DrawerFooter>
            <button
              className="raa-button raa-button-cancel tw-flex-1 md:tw-flex-none"
              disabled={inventeringPreliminarIsLoading}
              onClick={() =>
                navigate(
                  inventeringPreliminarData ? `/inventering/${inventeringPreliminarData.aktivitet_id}/preliminar` : '/',
                  { replace: true }
                )
              }
            >
              avbryt
            </button>
            {inventeringPreliminarData?.aktivitetstatus.status === AktivitetStatusEnum.PLANERAD && (
              <button
                className="raa-button raa-button-secondary tw-flex-1 md:tw-ml-auto md:tw-flex-none"
                disabled={inventeringPreliminarIsLoading || !inventeringPreliminarData}
                onClick={() => {
                  setDialog(DeleteInventeringPreliminarDialog, {
                    inventeringPreliminar: inventeringPreliminarData,
                    unsetDialog,
                    setDeleteSuccessful,
                    setDeleteLoading
                  });
                }}
              >
                radera
              </button>
            )}
            <button
              className="raa-button raa-button-confirm tw-flex-1 md:tw-w-32 md:tw-flex-none"
              type="submit"
              form="inventeringForm"
              disabled={inventeringPreliminarIsLoading || submitting || !dirtyForm}
            >
              {inventeringPreliminarIsLoading || submitting ? <RaaSpinner size="1rem" /> : 'Spara'}
            </button>
          </DrawerFooter>
        </DrawerContainer>
      )}
    </SpinnerContainer>
  );
};

export default InventeringRegistration;
